<template>
  <div class="min-h-screen w-full overflow-hidden selection:bg-brand-500 selection:text-white" :data-theme="theme">
    <!-- 背景 -->
    <div class="fixed inset-0 bg-[#07070a]"></div>
    <div class="fixed inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(59,130,246,0.14),_transparent_55%),radial-gradient(ellipse_at_bottom,_rgba(168,85,247,0.10),_transparent_55%)]"></div>
    <div class="fixed inset-0 bg-gradient-to-b from-black/40 via-black/10 to-black/60"></div>

    <main class="relative z-10 w-full max-w-[1280px] mx-auto px-5 sm:px-8 py-8">
      <!-- 顶部 -->
      <header class="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">我的项目</h1>
          <div class="mt-4 inline-flex items-center gap-1 p-1 rounded-full bg-white/5 border border-white/10 backdrop-blur">
            <button
              @click="activeTab = 'all'"
              :class="tabBtnClass(activeTab === 'all')"
            >
              全部
            </button>
            <button
              @click="activeTab = 'fav'"
              :class="tabBtnClass(activeTab === 'fav')"
            >
              我的收藏
            </button>
          </div>
        </div>

        <div class="w-full sm:w-[320px]">
          <div class="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 backdrop-blur focus-within:border-blue-500/50 transition-colors">
            <i class="fas fa-search text-gray-400 text-sm"></i>
            <input
              v-model.trim="keyword"
              class="w-full bg-transparent outline-none text-sm text-gray-200 placeholder:text-gray-500"
              placeholder="搜索项目名称"
              autocomplete="off"
            >
            <button
              v-if="keyword"
              @click="keyword = ''"
              class="text-gray-400 hover:text-gray-200 transition-colors"
              title="清空"
            >
              <i class="fas fa-times text-xs"></i>
            </button>
          </div>
        </div>
      </header>

      <!-- 网格 -->
      <section class="mt-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
        <!-- 新建项目 -->
        <button
          @click="createProject"
          class="group relative rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-blue-500/40 hover:bg-white/7 transition-colors text-left"
        >
          <div class="aspect-[16/10] w-full flex items-center justify-center bg-gradient-to-br from-white/4 to-white/0">
            <div class="w-12 h-12 rounded-2xl bg-white/6 border border-white/10 flex items-center justify-center text-white/90 group-hover:scale-[1.02] transition-transform">
              <i class="fas fa-plus text-lg"></i>
            </div>
          </div>
          <div class="p-3.5">
            <div class="font-bold text-sm text-white/90">新建项目</div>
            <div class="mt-1 text-xs text-gray-400">从空白开始</div>
          </div>
        </button>

        <!-- 项目卡片 -->
        <article
          v-for="p in filteredProjects"
          :key="p.uuid || p.id"
          class="group relative rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-white/20 hover:bg-white/7 transition-colors"
        >
          <div class="aspect-[16/10] w-full bg-white/4 relative">
            <img
              v-if="p.thumb"
              :src="p.thumb"
              class="w-full h-full object-cover"
              alt=""
            >
            <div v-else class="absolute inset-0 flex items-center justify-center text-white/15">
              <i class="fas fa-photo-film text-3xl"></i>
            </div>

            <button
              @click="toggleFav(p)"
              class="absolute top-2.5 right-2.5 w-9 h-9 rounded-xl bg-black/35 border border-white/10 backdrop-blur flex items-center justify-center text-white/70 hover:text-yellow-300 transition-colors"
              :title="p.fav ? '取消收藏' : '收藏'"
            >
              <i :class="['fas', p.fav ? 'fa-star text-yellow-300' : 'fa-star text-white/60']"></i>
            </button>
          </div>

          <div class="p-3.5">
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <div class="font-bold text-sm text-white/90 truncate">
                  {{ p.title || '未命名项目' }}
                </div>
                <div class="mt-1 text-[11px] text-gray-400">
                  {{ p.time || '' }}
                </div>
              </div>
              <button
                @click="openProject(p)"
                class="shrink-0 px-2.5 py-1.5 rounded-lg bg-blue-600/15 text-blue-300 border border-blue-500/20 hover:bg-blue-600/25 transition-colors text-[11px] font-bold"
                title="打开"
              >
                打开
              </button>
            </div>
          </div>
        </article>
      </section>

      <div v-if="errorText" class="mt-6 text-[12px] text-amber-200/90">
        {{ errorText }}
      </div>
      <div v-else-if="loading" class="mt-6 text-[12px] text-gray-500">
        加载中…
      </div>

      <div v-if="filteredProjects.length === 0" class="mt-16 text-center text-gray-400">
        <div class="text-white/20 text-4xl mb-3">
          <i class="fas fa-box-open"></i>
        </div>
        <div class="font-bold text-sm text-gray-200">暂无项目</div>
        <div class="mt-1 text-xs">点击“新建项目”开始</div>
      </div>
    </main>
  </div>
</template>

<script>
import { showErrorToast } from '../utils/toast'
import { fetchChatWorkflowSessionList } from '@/utils/chatWorkflowHistoryApi'

const FAV_KEY = 'chatWorkflowFavMap'

function safeJsonParse(s, fallback) {
  try {
    const v = JSON.parse(s)
    return v == null ? fallback : v
  } catch {
    return fallback
  }
}

export default {
  name: 'Projects',
  data() {
    return {
      theme: localStorage.getItem('theme') || 'dark',
      activeTab: 'all',
      keyword: '',
      projects: [],
      loading: false,
      errorText: ''
    }
  },
  computed: {
    filteredProjects() {
      const kw = (this.keyword || '').toLowerCase()
      return (this.projects || [])
        .filter(p => (this.activeTab === 'fav' ? !!p.fav : true))
        .filter(p => {
          if (!kw) return true
          return String(p.title || '').toLowerCase().includes(kw)
        })
    }
  },
  mounted() {
    this.loadProjects()
  },
  methods: {
    tabBtnClass(active) {
      return [
        'px-4 py-2 rounded-full text-xs font-bold transition-all',
        active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-200'
      ]
    },
    favMap() {
      const raw = localStorage.getItem(FAV_KEY)
      const map = safeJsonParse(raw || '{}', {})
      return map && typeof map === 'object' ? map : {}
    },
    setFav(uuid, v) {
      const id = String(uuid || '').trim()
      if (!id) return
      const map = this.favMap()
      map[id] = !!v
      localStorage.setItem(FAV_KEY, JSON.stringify(map))
    },
    async loadProjects() {
      this.loading = true
      this.errorText = ''
      try {
        const list = await fetchChatWorkflowSessionList({ limit: 200 })
        const fav = this.favMap()
        this.projects = (list || []).map(row => ({
          ...row,
          fav: !!fav[String(row.uuid || row.id || '')]
        }))
      } catch (e) {
        this.projects = []
        const status = e?.response?.status
        if (status === 401) {
          this.errorText = '登录后可查看对话工作流记录（未携带有效 token）。'
        } else {
          const body = e?.response?.data
          const msg =
            (typeof body === 'string' ? body : null) ||
            body?.message ||
            body?.msg ||
            body?.error ||
            e?.message ||
            '加载失败，请确认后端已部署 GET /chat_workflow_sessions 并配置代理。'
          this.errorText = msg.length > 220 ? msg.slice(0, 220) + '…' : msg
        }
      } finally {
        this.loading = false
      }
    },
    createProject() {
      // 对话工作流：无 session 参数即新建
      this.$router.push({ name: 'ConversationWorkflow', query: {} }).catch(() => {})
    },
    toggleFav(p) {
      try {
        const uuid = String(p && (p.uuid || p.id) ? (p.uuid || p.id) : '').trim()
        if (!uuid) return
        p.fav = !p.fav
        this.setFav(uuid, p.fav)
      } catch (e) {
        showErrorToast('操作失败')
      }
    },
    openProject(p) {
      const uuid = String(p && (p.uuid || p.id) ? (p.uuid || p.id) : '').trim()
      if (!uuid) return
      this.$router.push({ name: 'ConversationWorkflow', query: { session: uuid } }).catch(() => {})
    }
  }
}
</script>

<style scoped>
</style>

